import OpenAI from 'openai';
import { validateConfig } from './config';
import { supabase } from './supabase';
import { ContentGap } from '../types';

let openaiClient: OpenAI | null = null;

async function getOpenAIClient() {
  try {
    if (!openaiClient) {
      console.log('Initializing OpenAI client...');
      const config = await validateConfig();
      
      if (!config.OPENAI_API_KEY) {
        throw new Error('OpenAI API key is missing. Please check your configuration in the admin panel.');
      }

      openaiClient = new OpenAI({
        apiKey: config.OPENAI_API_KEY,
        dangerouslyAllowBrowser: true
      });
      console.log('OpenAI client initialized successfully');
    }
    return openaiClient;
  } catch (error) {
    console.error('Failed to initialize OpenAI client:', error);
    throw error;
  }
}

async function getAISettings() {
  const { data, error } = await supabase
    .from('ai_settings')
    .select('*')
    .eq('id', 1)
    .single();

  if (error) {
    console.error('Error fetching AI settings:', error);
    throw new Error('Failed to load AI settings');
  }

  return data;
}

// Obfuscate template variables to make them harder to trace
function obfuscateVariables(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    // Create a random-looking key that's actually deterministic
    const obfuscatedKey = btoa(key).replace(/[^a-zA-Z0-9]/g, '');
    result[obfuscatedKey] = value;
  }
  return result;
}

// Replace template variables with obfuscated versions
function replaceTemplateVariables(template: string, variables: Record<string, any>): string {
  const obfuscated = obfuscateVariables(variables);
  return template.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const obfuscatedKey = btoa(key.trim()).replace(/[^a-zA-Z0-9]/g, '');
    return obfuscated[obfuscatedKey] ?? match;
  });
}

export async function generateTitle(transcript: string): Promise<string> {
  try {
    const openai = await getOpenAIClient();
    const settings = await getAISettings();
    
    const prompt = replaceTemplateVariables(settings.title_prompt_template, {
      transcript
    });

    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: "You are a content specialist who creates concise, engaging titles for video content."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: 0.7,
      max_tokens: 60
    });

    const title = response.choices[0].message.content;
    if (!title) {
      throw new Error('No title generated');
    }

    return title.replace(/["']/g, '').trim();
  } catch (error) {
    console.error('Error generating title:', error);
    throw error;
  }
}

export async function generateContentGaps(transcript: string): Promise<ContentGap[]> {
  try {
    const openai = await getOpenAIClient();
    const settings = await getAISettings();
    
    const prompt = replaceTemplateVariables(settings.content_gaps_prompt_template, {
      transcript
    });

    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: "You are a content strategy expert who specializes in identifying trending topics and content opportunities."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: 0.7,
      max_tokens: 1000
    });

    const content = response.choices[0].message.content;
    if (!content) {
      throw new Error('No content returned from OpenAI');
    }

    try {
      const parsed = JSON.parse(content);
      if (!Array.isArray(parsed)) {
        throw new Error('Invalid response format: expected an array');
      }
      
      const validatedGaps = parsed.map(item => {
        if (!item.title || !item.description) {
          throw new Error('Invalid item format: missing title or description');
        }
        return {
          title: String(item.title),
          description: String(item.description)
        };
      });

      return validatedGaps;
    } catch (parseError) {
      console.error('Error parsing OpenAI response:', parseError);
      throw new Error('Failed to parse content gaps response');
    }
  } catch (error) {
    console.error('Error generating content gaps:', error);
    throw error;
  }
}

export async function generateCaption(
  transcript: string,
  config: {
    tone: string;
    length: 'short' | 'medium' | 'long';
    includeHashtags: boolean;
    includeContentGaps: boolean;
    customInstructions?: string;
    customPrompt?: string;
  }
): Promise<string> {
  try {
    const openai = await getOpenAIClient();
    const settings = await getAISettings();
    
    // Validate tone value
    const validTones = [
      'professional', 'casual', 'friendly', 'humorous',
      'thought-leader', 'industry-insider', 'inspirational'
    ];
    if (!validTones.includes(config.tone)) {
      throw new Error(`Invalid tone value: ${config.tone}`);
    }

    const prompt = config.customPrompt || replaceTemplateVariables(settings.caption_prompt_template, {
      transcript,
      tone: config.tone,
      length: config.length,
      includeHashtags: config.includeHashtags,
      customInstructions: config.customInstructions
    });

    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: "You are a professional social media expert who specializes in creating engaging LinkedIn content that drives engagement and maintains professional standards."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: 0.7,
      max_tokens: 1000
    });

    const content = response.choices[0].message.content;
    if (!content) {
      throw new Error('No content returned from OpenAI');
    }

    return content;
  } catch (error) {
    console.error('Error generating caption:', error);
    throw error;
  }
}